import { z } from 'zod';

// src/enums.ts
var SALE_UNITS = ["metro", "unidade", "pacote", "kg", "conjunto"];
var SALE_UNIT_LABELS = {
  metro: {
    singular: "metro",
    plural: "metros",
    abbr: "m",
    priceSuffix: "/metro",
    defaultStep: 0.5
  },
  unidade: {
    singular: "unidade",
    plural: "unidades",
    abbr: "un",
    priceSuffix: "/un",
    defaultStep: 1
  },
  pacote: {
    singular: "pacote",
    plural: "pacotes",
    abbr: "pct",
    priceSuffix: "/pacote",
    defaultStep: 1
  },
  kg: { singular: "kg", plural: "kg", abbr: "kg", priceSuffix: "/kg", defaultStep: 0.1 },
  conjunto: {
    singular: "conjunto",
    plural: "conjuntos",
    abbr: "cj",
    priceSuffix: "/conjunto",
    defaultStep: 1
  }
};
var AVAILABILITIES = [
  "em_estoque",
  "ultimas_unidades",
  "sob_consulta",
  "esgotado"
];
var AVAILABILITY_LABELS = {
  em_estoque: "Em estoque",
  ultimas_unidades: "\xDAltimas unidades",
  sob_consulta: "Sob consulta",
  esgotado: "Esgotado"
};
var PRODUCT_STATUSES = ["rascunho", "ativo", "inativo", "arquivado"];
var ORDER_STATUSES = [
  "novo",
  "em_atendimento",
  "aguardando_pagamento",
  "pago",
  "separado",
  "em_entrega",
  "entregue",
  "retirado",
  "cancelado",
  "perdido"
];
var ORDER_STATUS_LABELS = {
  novo: "Novo",
  em_atendimento: "Em atendimento",
  aguardando_pagamento: "Aguardando pagamento",
  pago: "Pago",
  separado: "Separado",
  em_entrega: "Em entrega",
  entregue: "Entregue",
  retirado: "Retirado",
  cancelado: "Cancelado",
  perdido: "Perdido"
};
var CLOSED_ORDER_STATUSES = [
  "entregue",
  "retirado",
  "cancelado",
  "perdido"
];
var DELIVERY_TYPES = ["retirada", "motoboy", "combinar"];
var IMPORT_KINDS = ["produtos", "precos", "estoque"];
var EXPORT_FORMATS = ["csv", "xlsx", "zip"];

// src/money.ts
var BRL_FORMATTER = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL"
});
function normalizeSpaces(value) {
  return value.replace(/\s/g, " ");
}
function formatBRL(cents) {
  return normalizeSpaces(BRL_FORMATTER.format(cents / 100));
}
function formatAmount(cents) {
  return normalizeSpaces(
    (cents / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  );
}
function parseBRL(input) {
  if (input === null || input === void 0) return null;
  if (typeof input === "number") {
    return Number.isFinite(input) ? Math.round(input * 100) : null;
  }
  const cleaned = input.trim().replace(/^R\$\s*/i, "").replace(/\s/g, "");
  if (cleaned === "") return null;
  if (!/^-?[\d.,]+$/.test(cleaned)) return null;
  const negative = cleaned.startsWith("-");
  const digitsOnly = negative ? cleaned.slice(1) : cleaned;
  const lastComma = digitsOnly.lastIndexOf(",");
  const lastDot = digitsOnly.lastIndexOf(".");
  let normalized;
  if (lastComma >= 0 && lastDot >= 0) {
    const decimalSep = lastComma > lastDot ? "," : ".";
    const thousandSep = decimalSep === "," ? "." : ",";
    normalized = digitsOnly.split(thousandSep).join("").replace(decimalSep, ".");
  } else if (lastComma >= 0) {
    normalized = digitsOnly.replace(/\./g, "").replace(",", ".");
  } else if (lastDot >= 0) {
    const decimals = digitsOnly.length - lastDot - 1;
    const multipleDots = digitsOnly.indexOf(".") !== lastDot;
    normalized = decimals === 3 || multipleDots ? digitsOnly.split(".").join("") : digitsOnly;
  } else {
    normalized = digitsOnly;
  }
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100) * (negative ? -1 : 1);
}
function multiplyQuantity(unitPrice, quantity) {
  const scaled = Math.round(quantity * 1e3);
  return Math.round(unitPrice * scaled / 1e3);
}
function sumCents(values) {
  return values.reduce((total, value) => total + value, 0);
}
function discountPercent(from, to) {
  if (from <= 0 || to >= from) return 0;
  return Math.round((from - to) / from * 100);
}

// src/orders.ts
var KANBAN_COLUMNS = [
  { key: "novo", label: "Novo", statuses: ["novo"] },
  { key: "em_atendimento", label: "Em atendimento", statuses: ["em_atendimento"] },
  {
    key: "aguardando_pagamento",
    label: "Aguardando pagamento",
    statuses: ["aguardando_pagamento"]
  },
  { key: "pago", label: "Pago", statuses: ["pago"] },
  { key: "separado", label: "Separado", statuses: ["separado"] },
  { key: "em_entrega", label: "Em entrega", statuses: ["em_entrega"] },
  { key: "entregue", label: "Entregue", statuses: ["entregue"] },
  { key: "retirado", label: "Retirado", statuses: ["retirado"] },
  { key: "cancelado", label: "Cancelado", statuses: ["cancelado"] },
  { key: "perdido", label: "Perdido", statuses: ["perdido"] }
];
function isClosedStatus(status) {
  return CLOSED_ORDER_STATUSES.includes(status);
}
function requiresLostReason(status) {
  return status === "perdido" || status === "cancelado";
}
function acceptsFinalValue(status) {
  return status === "pago" || status === "separado" || status === "em_entrega" || status === "entregue" || status === "retirado";
}
function canTransition(_de, _para) {
  return true;
}
var DEFAULT_STATUS_MESSAGES = {
  novo: "Ol\xE1, {nome}! Aqui \xE9 da {loja}. Recebemos seu pedido {codigo} e j\xE1 vamos cuidar dele. Qualquer d\xFAvida, \xE9 s\xF3 chamar por aqui! \u{1F60A}",
  em_atendimento: "Oi, {nome}! Seu pedido {codigo} est\xE1 em atendimento. Em breve retornamos com os pr\xF3ximos passos. \u{1F9F5}",
  aguardando_pagamento: "{nome}, seu pedido {codigo} est\xE1 reservado! Para confirmar, o valor \xE9 {valor}.\n\n\u{1F4B3} PIX (CNPJ): {pix} \u2014 {loja}\n\nAssim que recebermos o pagamento, seguimos com a separa\xE7\xE3o. Pode me enviar o comprovante por aqui!",
  pago: "Pagamento confirmado, {nome}! \u2705 J\xE1 estamos separando seu pedido {codigo}. Logo te aviso quando estiver pronto.",
  separado: "{nome}, seu pedido {codigo} est\xE1 separado e pronto! \u{1F389} Vamos combinar a entrega ou retirada?",
  em_entrega: "{nome}, seu pedido {codigo} saiu para entrega! \u{1F697} Logo chega at\xE9 voc\xEA.",
  entregue: "Pedido {codigo} entregue! \u{1F4E6} Obrigado pela prefer\xEAncia, {nome}. Qualquer coisa, estamos \xE0 disposi\xE7\xE3o. \u{1F499}",
  retirado: "Pedido {codigo} retirado com sucesso! \u{1F6CD}\uFE0F Obrigado por comprar na {loja}, {nome}. Volte sempre! \u{1F499}",
  cancelado: "Oi, {nome}. Seu pedido {codigo} foi cancelado. Se precisar de qualquer coisa ou quiser retomar, \xE9 s\xF3 chamar por aqui!",
  perdido: "Ol\xE1, {nome}! Passando para saber se ainda tem interesse no pedido {codigo}. Estamos \xE0 disposi\xE7\xE3o para ajudar no que precisar! \u{1F60A}"
};
function preencherMensagem(template, vars) {
  return template.replace(/\{nome\}/g, vars.nome ?? "").replace(/\{codigo\}/g, vars.codigo ?? "").replace(/\{valor\}/g, vars.valor ?? "").replace(/\{loja\}/g, vars.loja ?? "").replace(/\{pix\}/g, vars.pix ?? "");
}
var COUPON_REJECTION_MESSAGES = {
  inexistente: "Cupom n\xE3o encontrado.",
  inativo: "Este cupom n\xE3o est\xE1 mais valendo.",
  fora_da_data: "Este cupom est\xE1 fora do prazo.",
  esgotado: "Este cupom j\xE1 atingiu o limite de usos.",
  subtotal_insuficiente: "Seu pedido ainda n\xE3o atingiu o valor m\xEDnimo deste cupom."
};
function applyCoupon(subtotal, coupon, agora = /* @__PURE__ */ new Date()) {
  if (!coupon) return recusa("inexistente");
  if (coupon.active === false) return recusa("inativo");
  const inicio = paraData(coupon.startsAt);
  const fim = paraData(coupon.endsAt);
  if (inicio && agora < inicio) return recusa("fora_da_data");
  if (fim && agora > fim) return recusa("fora_da_data");
  if (coupon.usageLimit != null && (coupon.usedCount ?? 0) >= coupon.usageLimit) {
    return recusa("esgotado");
  }
  if (coupon.minSubtotal != null && subtotal < coupon.minSubtotal) {
    return recusa("subtotal_insuficiente");
  }
  const bruto = coupon.type === "percent" ? Math.round(subtotal * coupon.value / 100) : Math.round(coupon.value);
  return { valid: true, discount: Math.max(0, Math.min(bruto, subtotal)) };
}
function recusa(reason) {
  return { valid: false, reason, message: COUPON_REJECTION_MESSAGES[reason] };
}
function paraData(valor) {
  if (!valor) return null;
  const data = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}
function calculateTotals(lines, discount = 0) {
  const subtotal = lines.reduce((soma, linha) => soma + lineTotal(linha), 0);
  const desconto = Math.max(0, Math.min(discount, subtotal));
  return {
    itemCount: lines.length,
    subtotal,
    discount: desconto,
    total: subtotal - desconto
  };
}
function lineTotal(line) {
  return Math.round(line.unitPrice * line.quantity);
}
function formatOrderCode(year, sequence) {
  return `CP-${year}-${String(sequence).padStart(4, "0")}`;
}
var ORDER_CODE = /^CP-(\d{4})-(\d{4,})$/;
function parseOrderCode(code) {
  const encontrado = ORDER_CODE.exec(code.trim().toUpperCase());
  if (!encontrado) return null;
  return { year: Number(encontrado[1]), sequence: Number(encontrado[2]) };
}
var RECOVERY_AFTER_HOURS = 24;
function needsRecovery(order, agora = /* @__PURE__ */ new Date()) {
  if (isClosedStatus(order.status)) return false;
  if (order.status !== "novo") return false;
  const criado = paraData(order.createdAt);
  if (!criado) return false;
  return agora.getTime() - criado.getTime() >= RECOVERY_AFTER_HOURS * 60 * 60 * 1e3;
}

// src/slug.ts
var COMBINING_MARKS = /[̀-ͯ]/g;
function stripAccents(input) {
  return input.normalize("NFD").replace(COMBINING_MARKS, "");
}
function slugify(input) {
  return input.normalize("NFD").replace(COMBINING_MARKS, "").replace(/[çÇ]/g, "c").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 96);
}
function normalizeSearchTerm(input) {
  return input.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase().replace(/\s+/g, " ").trim();
}
function titleCaseFromErp(input) {
  const trimmed = input.trim().replace(/\s+/g, " ");
  if (trimmed !== trimmed.toUpperCase()) return trimmed;
  const lowercaseWords = /* @__PURE__ */ new Set([
    "de",
    "da",
    "do",
    "das",
    "dos",
    "e",
    "em",
    "com",
    "para",
    "por",
    "a",
    "o",
    "as",
    "os"
  ]);
  return trimmed.toLowerCase().split(" ").map((word, index) => {
    if (index > 0 && lowercaseWords.has(word)) return word;
    if (/^\d/.test(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(" ");
}

// src/import.ts
var IMPORT_FIELDS = [
  "skuFutura",
  "name",
  "shortDesc",
  "description",
  "categoria",
  "marca",
  "unit",
  "saleStep",
  "minQty",
  "packSize",
  "price",
  "promoPrice",
  "qty",
  "status",
  "availability"
];
var IMPORT_FIELD_LABELS = {
  skuFutura: "C\xF3digo no Futura",
  name: "Nome",
  shortDesc: "Resumo",
  description: "Descri\xE7\xE3o",
  categoria: "Categoria (nome)",
  marca: "Marca (nome)",
  unit: "Unidade de venda",
  saleStep: "Passo de venda",
  minQty: "Quantidade m\xEDnima",
  packSize: "Itens por pacote",
  price: "Pre\xE7o",
  promoPrice: "Pre\xE7o promocional",
  qty: "Quantidade em estoque",
  status: "Status",
  availability: "Disponibilidade"
};
var FIELDS_BY_KIND = {
  produtos: [...IMPORT_FIELDS],
  precos: ["skuFutura", "price", "promoPrice"],
  estoque: ["skuFutura", "qty", "availability"]
};
var PALPITES = [
  { campo: "skuFutura", padroes: [/^(cod|codigo|sku|referencia|ref)/, /produto.*cod/] },
  { campo: "name", padroes: [/^(descricao|desc|nome|produto|titulo)$/, /^descricao/] },
  { campo: "price", padroes: [/(vlr|valor|preco|price).*(venda|varejo)/, /^(preco|valor|vlr)$/] },
  { campo: "promoPrice", padroes: [/(promo|oferta|desconto)/] },
  { campo: "qty", padroes: [/^(qtd|qtde|quantidade|estoque|saldo)/] },
  { campo: "unit", padroes: [/^(un|unid|unidade|medida)/] },
  { campo: "categoria", padroes: [/^(categoria|grupo|secao|departamento)/] },
  { campo: "marca", padroes: [/^(marca|fabricante|fornecedor)/] },
  { campo: "packSize", padroes: [/(embalagem|pacote|caixa|múltiplo|multiplo)/] },
  { campo: "shortDesc", padroes: [/(resumo|complemento|detalhe)/] }
];
function guessColumnMap(headers, campos) {
  const mapa = {};
  const usados = /* @__PURE__ */ new Set();
  for (const header of headers) {
    const normalizado = stripAccents(header).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    const achado = PALPITES.find(
      (palpite) => campos.includes(palpite.campo) && !usados.has(palpite.campo) && palpite.padroes.some((padrao) => padrao.test(normalizado))
    );
    mapa[header] = achado?.campo ?? "";
    if (achado) usados.add(achado.campo);
  }
  return mapa;
}
function normalizeUnit(valor) {
  if (!valor) return null;
  const v = stripAccents(valor).toLowerCase().replace(/[^a-z]/g, "");
  if (!v) return null;
  if (SALE_UNITS.includes(v)) return v;
  const tabela = {
    m: "metro",
    mt: "metro",
    mts: "metro",
    metros: "metro",
    ml: "metro",
    un: "unidade",
    und: "unidade",
    unid: "unidade",
    unidades: "unidade",
    pc: "unidade",
    pca: "unidade",
    peca: "unidade",
    pecas: "unidade",
    pct: "pacote",
    pcte: "pacote",
    pacotes: "pacote",
    cx: "pacote",
    caixa: "pacote",
    kg: "kg",
    kgs: "kg",
    quilo: "kg",
    quilos: "kg",
    g: "kg",
    cj: "conjunto",
    conj: "conjunto",
    kit: "conjunto",
    jogo: "conjunto"
  };
  return tabela[v] ?? null;
}
function normalizeQuantityValue(valor) {
  if (valor === null || valor === void 0 || valor === "") return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  const centavos = parseBRL(valor);
  return centavos === null ? null : centavos / 100;
}
function normalizeStatus(valor) {
  if (!valor) return null;
  const v = stripAccents(valor).toLowerCase().trim();
  if (["ativo", "a", "s", "sim", "1", "true", "habilitado"].includes(v)) return "ativo";
  if (["inativo", "i", "n", "nao", "0", "false", "desabilitado"].includes(v)) return "inativo";
  return null;
}
function normalizeAvailability(valor) {
  if (!valor) return null;
  const v = stripAccents(valor).toLowerCase().replace(/[^a-z]/g, "_");
  return AVAILABILITIES.includes(v) ? v : null;
}
function applyMarkup(preco, markupPercent = 0) {
  if (!markupPercent) return preco;
  return Math.max(0, Math.round(preco * (1 + markupPercent / 100)));
}
function parseRow(bruta, mapa, linha, opcoes = {}) {
  const saida = { linha, skuFutura: null, erros: [], avisos: [] };
  const valorDe = (campo) => {
    const coluna = Object.keys(mapa).find((chave) => mapa[chave] === campo);
    if (!coluna) return void 0;
    const valor = bruta[coluna];
    return valor === void 0 || valor === null ? void 0 : String(valor).trim();
  };
  const sku = valorDe("skuFutura");
  if (!sku) {
    saida.erros.push("Sem c\xF3digo do Futura \u2014 n\xE3o d\xE1 para casar com o cat\xE1logo");
    return saida;
  }
  saida.skuFutura = sku;
  const nome = valorDe("name");
  if (nome) {
    saida.name = titleCaseFromErp(nome);
    saida.slug = slugify(saida.name);
    if (saida.name.length > 160) {
      saida.name = saida.name.slice(0, 160);
      saida.avisos.push("Nome truncado em 160 caracteres");
    }
  }
  for (const campo of ["shortDesc", "description", "categoria", "marca"]) {
    const valor = valorDe(campo);
    if (valor) saida[campo] = valor;
  }
  const unidadeBruta = valorDe("unit");
  if (unidadeBruta) {
    const unidade = normalizeUnit(unidadeBruta);
    if (unidade) saida.unit = unidade;
    else saida.avisos.push(`Unidade "${unidadeBruta}" n\xE3o reconhecida \u2014 mantida a atual`);
  }
  for (const campo of ["saleStep", "minQty"]) {
    const valor = normalizeQuantityValue(valorDe(campo));
    if (valor !== null) {
      if (valor > 0) saida[campo] = valor;
      else saida.erros.push(`${IMPORT_FIELD_LABELS[campo]} precisa ser maior que zero`);
    }
  }
  const pacote = normalizeQuantityValue(valorDe("packSize"));
  if (pacote !== null && pacote >= 1) saida.packSize = Math.round(pacote);
  const precoBruto = valorDe("price");
  if (precoBruto !== void 0 && precoBruto !== "") {
    const preco = parseBRL(precoBruto);
    if (preco === null) saida.erros.push(`Pre\xE7o "${precoBruto}" n\xE3o \xE9 um valor v\xE1lido`);
    else if (preco < 0) saida.erros.push("Pre\xE7o negativo");
    else saida.price = applyMarkup(preco, opcoes.markupPercent);
  }
  const promoBruta = valorDe("promoPrice");
  if (promoBruta !== void 0) {
    if (promoBruta === "" || promoBruta === "0") {
      saida.promoPrice = null;
    } else {
      const promo = parseBRL(promoBruta);
      if (promo === null) saida.avisos.push(`Pre\xE7o promocional "${promoBruta}" ignorado`);
      else if (saida.price !== void 0 && promo >= saida.price) {
        saida.avisos.push("Promo\xE7\xE3o maior ou igual ao pre\xE7o \u2014 ignorada");
        saida.promoPrice = null;
      } else saida.promoPrice = promo;
    }
  }
  const quantidade = normalizeQuantityValue(valorDe("qty"));
  if (quantidade !== null) {
    saida.qty = Math.max(0, quantidade);
    if (quantidade < 0) saida.avisos.push("Estoque negativo no ERP \u2014 tratado como zero");
  }
  const disponibilidade = normalizeAvailability(valorDe("availability"));
  if (disponibilidade) saida.availability = disponibilidade;
  const status = normalizeStatus(valorDe("status"));
  if (status) saida.status = status;
  return saida;
}
function diffRow(linha, existente, opcoes = {}) {
  const base = {
    linha: linha.linha,
    skuFutura: linha.skuFutura,
    nome: linha.name ?? existente?.name ?? null,
    acao: "sem_mudanca",
    mudancas: {},
    erros: linha.erros,
    avisos: linha.avisos
  };
  if (linha.erros.length > 0) return { ...base, acao: "erro" };
  if (!existente) {
    if (!opcoes.criarAusentes) {
      return {
        ...base,
        acao: "ignorar",
        avisos: [...base.avisos, "Produto n\xE3o existe no cat\xE1logo e a cria\xE7\xE3o est\xE1 desligada"]
      };
    }
    if (linha.price === void 0) {
      return { ...base, acao: "erro", erros: ["Produto novo precisa de pre\xE7o"] };
    }
    return { ...base, acao: "criar", mudancas: { criado: { de: null, para: linha.name } } };
  }
  const mudancas = {};
  if (opcoes.atualizarNome && linha.name && linha.name !== existente.name) {
    mudancas.name = { de: existente.name, para: linha.name };
  }
  if (linha.price !== void 0 && linha.price !== existente.price) {
    mudancas.price = { de: existente.price, para: linha.price };
  }
  if (linha.promoPrice !== void 0 && linha.promoPrice !== existente.promoPrice) {
    mudancas.promoPrice = { de: existente.promoPrice, para: linha.promoPrice };
  }
  if (linha.unit && linha.unit !== existente.unit) {
    mudancas.unit = { de: existente.unit, para: linha.unit };
  }
  if (linha.qty !== void 0 && linha.qty !== existente.qty) {
    mudancas.qty = { de: existente.qty, para: linha.qty };
  }
  return {
    ...base,
    acao: Object.keys(mudancas).length > 0 ? "atualizar" : "sem_mudanca",
    mudancas
  };
}
function summarize(diffs) {
  const vistos = /* @__PURE__ */ new Map();
  for (const diff of diffs) {
    if (!diff.skuFutura) continue;
    vistos.set(diff.skuFutura, (vistos.get(diff.skuFutura) ?? 0) + 1);
  }
  return {
    total: diffs.length,
    criar: diffs.filter((d) => d.acao === "criar").length,
    atualizar: diffs.filter((d) => d.acao === "atualizar").length,
    semMudanca: diffs.filter((d) => d.acao === "sem_mudanca").length,
    ignorar: diffs.filter((d) => d.acao === "ignorar").length,
    erro: diffs.filter((d) => d.acao === "erro").length,
    duplicados: [...vistos.entries()].filter(([, n]) => n > 1).map(([sku]) => sku)
  };
}
function escapeCsvInjection(valor) {
  return /^[=+\-@\t\r]/.test(valor) ? `'${valor}` : valor;
}
function toCsvCell(valor) {
  if (valor === null || valor === void 0) return "";
  const texto = escapeCsvInjection(String(valor));
  return /[";\n\r]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
}

// src/permissions.ts
var PERMISSION_MODULES = [
  "catalogo",
  "midia",
  "estoque",
  "importacao",
  "exportacao",
  "pedidos",
  "clientes",
  "cupons",
  "cms",
  "cursos",
  "tarefas",
  "relatorios",
  "identidade",
  "configuracoes",
  "usuarios"
];
var PERMISSION_ACTIONS = ["ver", "criar", "editar", "excluir"];
var MODULE_LABELS = {
  catalogo: "Cat\xE1logo",
  midia: "M\xEDdia",
  estoque: "Estoque",
  importacao: "Importa\xE7\xE3o",
  exportacao: "Exporta\xE7\xE3o",
  pedidos: "Pedidos",
  clientes: "Clientes",
  cupons: "Cupons",
  cms: "Conte\xFAdo do site",
  cursos: "Cursos e workshops",
  tarefas: "Tarefas internas",
  relatorios: "Relat\xF3rios",
  identidade: "Identidade visual",
  configuracoes: "Configura\xE7\xF5es",
  usuarios: "Usu\xE1rios e permiss\xF5es"
};
var ACTION_LABELS = {
  ver: "Ver",
  criar: "Criar",
  editar: "Editar",
  excluir: "Excluir"
};
function allPermissions() {
  return PERMISSION_MODULES.flatMap(
    (module) => PERMISSION_ACTIONS.map((action) => `${module}:${action}`)
  );
}
function hasPermission(granted, required) {
  if (granted.includes("*")) return true;
  if (granted.includes(required)) return true;
  const [module] = required.split(":");
  return module !== void 0 && granted.includes(`${module}:*`);
}
function hasAllPermissions(granted, required) {
  return required.every((permission) => hasPermission(granted, permission));
}
function hasAnyPermission(granted, required) {
  return required.some((permission) => hasPermission(granted, permission));
}
function expandPermissions(granted) {
  return allPermissions().filter((permission) => hasPermission(granted, permission));
}
var DEFAULT_ROLES = [
  {
    name: "Dono",
    description: "Acesso total, inclusive usu\xE1rios e configura\xE7\xF5es",
    permissions: ["*"]
  },
  {
    name: "Gerente",
    description: "Cat\xE1logo, pedidos, clientes, promo\xE7\xF5es e relat\xF3rios",
    permissions: [
      "catalogo:*",
      "midia:*",
      "estoque:*",
      "importacao:*",
      "exportacao:*",
      "pedidos:*",
      "clientes:*",
      "cupons:*",
      "cms:*",
      "cursos:*",
      "tarefas:*",
      "relatorios:ver"
    ]
  },
  {
    name: "Vendedor",
    description: "Atende os pedidos que chegam do site",
    permissions: [
      "pedidos:ver",
      "pedidos:editar",
      "clientes:ver",
      "catalogo:ver",
      "tarefas:ver",
      "tarefas:editar"
    ]
  },
  {
    name: "Estoquista",
    description: "Ajusta estoque e importa planilhas do Futura",
    permissions: ["catalogo:ver", "estoque:*", "importacao:*", "exportacao:ver"]
  },
  {
    name: "Marketing",
    description: "Banners, cole\xE7\xF5es, cupons e SEO",
    permissions: ["cms:*", "cupons:*", "midia:*", "catalogo:ver", "relatorios:ver"]
  }
];

// src/pricing.ts
function isPromoActive(input, at = /* @__PURE__ */ new Date()) {
  if (input.promoPrice === null || input.promoPrice === void 0) return false;
  if (input.promoPrice >= input.price) return false;
  const starts = toDate(input.promoStartsAt);
  const ends = toDate(input.promoEndsAt);
  if (starts && at < starts) return false;
  if (ends && at > ends) return false;
  return true;
}
function resolvePrice(input, options = {}) {
  const { quantity = 1, groupId = null, wholesaleEnabled = false, at = /* @__PURE__ */ new Date() } = options;
  let unitPrice = input.price;
  let source = "tabela";
  if (isPromoActive(input, at)) {
    unitPrice = input.promoPrice;
    source = "promocao";
  }
  const tier = bestTier(input.tiers ?? [], quantity, groupId, wholesaleEnabled);
  if (tier && tier.price < unitPrice) {
    unitPrice = tier.price;
    source = tier.groupId === null ? "faixa" : "grupo";
  }
  return {
    unitPrice,
    compareAt: unitPrice < input.price ? input.price : null,
    source
  };
}
function bestTier(tiers, quantity, groupId, wholesaleEnabled) {
  const applicable = tiers.filter((tier) => {
    if (tier.minQty > quantity) return false;
    if (tier.groupId === null) return true;
    return wholesaleEnabled && tier.groupId === groupId;
  });
  if (applicable.length === 0) return null;
  return applicable.reduce((best, tier) => {
    if (tier.minQty !== best.minQty) return tier.minQty > best.minQty ? tier : best;
    if (tier.groupId !== null && best.groupId === null) return tier;
    if (tier.groupId === null && best.groupId !== null) return best;
    return tier.price < best.price ? tier : best;
  });
}
function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// src/quantity.ts
function defaultRulesFor(unit) {
  const step = SALE_UNIT_LABELS[unit].defaultStep;
  return { step, min: step };
}
function normalizeQuantity(quantity, rules) {
  const { step, min, max } = rules;
  if (!Number.isFinite(quantity) || quantity <= 0) return min;
  const steps = Math.round(quantity / step);
  let normalized = round3(steps * step);
  if (normalized < min) normalized = min;
  if (max !== void 0 && normalized > max) normalized = max;
  return normalized;
}
function incrementQuantity(quantity, rules) {
  return normalizeQuantity(round3(quantity + rules.step), rules);
}
function decrementQuantity(quantity, rules) {
  return normalizeQuantity(round3(quantity - rules.step), rules);
}
function formatQuantity(quantity, unit) {
  const labels = SALE_UNIT_LABELS[unit];
  const number = formatNumber(quantity);
  if (unit === "unidade") return `${number} un`;
  if (unit === "kg") return `${number} kg`;
  const noun = quantity === 1 ? labels.singular : labels.plural;
  return `${number} ${noun}`;
}
function formatUnitPriceSuffix(unit) {
  return SALE_UNIT_LABELS[unit].priceSuffix;
}
function formatPackSize(packSize) {
  if (!packSize || packSize <= 1) return null;
  return `Pacote com ${packSize} un`;
}
function formatNumber(value) {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}
function round3(value) {
  return Math.round(value * 1e3) / 1e3;
}

// src/business-hours.ts
var STORE_TIME_ZONE = "America/Sao_Paulo";
var WEEKDAY_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "s\xE1b"];
var WEEKDAY_LONG = [
  "domingo",
  "segunda-feira",
  "ter\xE7a-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "s\xE1bado"
];
var DEFAULT_SCHEDULE = {
  timeZone: STORE_TIME_ZONE,
  hours: [
    { weekday: 0, opensAt: "00:00", closesAt: "00:00", closed: true },
    { weekday: 1, opensAt: "09:00", closesAt: "18:00", closed: false },
    { weekday: 2, opensAt: "09:00", closesAt: "18:00", closed: false },
    { weekday: 3, opensAt: "09:00", closesAt: "18:00", closed: false },
    { weekday: 4, opensAt: "09:00", closesAt: "18:00", closed: false },
    { weekday: 5, opensAt: "09:00", closesAt: "18:00", closed: false },
    { weekday: 6, opensAt: "09:00", closesAt: "16:00", closed: false }
  ],
  holidays: []
};
function zonedNow(date, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hour12: false
  });
  const parts = formatter.formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value ?? "";
  const weekdayMap = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6
  };
  const hour = Number(get("hour")) % 24;
  const minute = Number(get("minute"));
  return {
    isoDate: `${get("year")}-${get("month")}-${get("day")}`,
    weekday: weekdayMap[get("weekday")] ?? 0,
    minutes: hour * 60 + minute
  };
}
function toMinutes(time) {
  const [hours = "0", minutes = "0"] = time.split(":");
  return Number(hours) * 60 + Number(minutes);
}
function formatTime(time) {
  const [hours = "0", minutes = "00"] = time.split(":");
  const h = Number(hours);
  return `${h}h${minutes}`;
}
function getStoreStatus(schedule = DEFAULT_SCHEDULE, at = /* @__PURE__ */ new Date()) {
  const now = zonedNow(at, schedule.timeZone || STORE_TIME_ZONE);
  const holiday = schedule.holidays.find((item) => item.date === now.isoDate);
  const today = schedule.hours.find((hour) => hour.weekday === now.weekday);
  const isOpen = !holiday && !!today && !today.closed && now.minutes >= toMinutes(today.opensAt) && now.minutes < toMinutes(today.closesAt);
  if (isOpen && today) {
    return {
      isOpen: true,
      label: "Estamos abertos: Av. Sete de Setembro, 1460 - Vila Galv\xE3o, Guarulhos",
      message: `Estamos abertos at\xE9 ${formatTime(today.closesAt)}. Respondemos seu pedido em instantes.`
    };
  }
  const next = findNextOpening(schedule, now);
  if (!next) {
    return {
      isOpen: false,
      label: "Fechado",
      message: "Estamos fechados no momento. Envie seu pedido e responderemos assim que abrirmos.",
      ...holiday ? { holiday } : {}
    };
  }
  const timeLabel = next.isToday ? `hoje a partir das ${formatTime(next.time)}` : next.isTomorrow ? `amanh\xE3 a partir das ${formatTime(next.time)}` : `${next.weekdayLabel} a partir das ${formatTime(next.time)}`;
  const shortLabel = next.isToday ? `hoje \xE0s ${formatTime(next.time)}` : next.isTomorrow ? `amanh\xE3 \xE0s ${formatTime(next.time)}` : `${WEEKDAY_SHORT[next.weekday]} \xE0s ${formatTime(next.time)}`;
  const reason = holiday ? `Fechado: ${holiday.label}.` : "Estamos fechados.";
  return {
    isOpen: false,
    label: `Fechado. Abrimos ${shortLabel}.`,
    message: `${reason} Envie seu pedido, respondemos ${timeLabel}.`,
    opensAgain: next,
    ...holiday ? { holiday } : {}
  };
}
function findNextOpening(schedule, now) {
  for (let offset = 0; offset < 8; offset += 1) {
    const weekday = (now.weekday + offset) % 7;
    const hour = schedule.hours.find((item) => item.weekday === weekday);
    if (!hour || hour.closed) continue;
    const isToday = offset === 0;
    if (isToday && now.minutes >= toMinutes(hour.opensAt)) continue;
    const date = addDaysIso(now.isoDate, offset);
    if (schedule.holidays.some((holiday) => holiday.date === date)) continue;
    return {
      weekday,
      weekdayLabel: WEEKDAY_LONG[weekday] ?? "",
      time: hour.opensAt,
      isToday,
      isTomorrow: offset === 1
    };
  }
  return void 0;
}
function addDaysIso(isoDate, days) {
  const [year = "1970", month = "01", day = "01"] = isoDate.split("-");
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
function formatScheduleLines(schedule = DEFAULT_SCHEDULE) {
  const lines = [];
  const open = schedule.hours.filter((hour) => !hour.closed).sort((a, b) => a.weekday - b.weekday);
  let index = 0;
  while (index < open.length) {
    const start = open[index];
    if (!start) break;
    let end = index;
    while (end + 1 < open.length && open[end + 1]?.weekday === (open[end]?.weekday ?? 0) + 1 && open[end + 1]?.opensAt === start.opensAt && open[end + 1]?.closesAt === start.closesAt) {
      end += 1;
    }
    const from = WEEKDAY_SHORT[start.weekday] ?? "";
    const to = WEEKDAY_SHORT[open[end]?.weekday ?? start.weekday] ?? "";
    const days = end > index ? `${cap(from)} a ${to}` : cap(from);
    lines.push(`${days}: ${formatTime(start.opensAt)} \xE0s ${formatTime(start.closesAt)}`);
    index = end + 1;
  }
  return lines;
}
function cap(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// src/consent.ts
var CONSENT_VERSION = "2026-09";
var CONSENT_CATEGORIES = [
  {
    key: "essencial",
    label: "Essenciais",
    descricao: "Mant\xEAm o carrinho, a sua conta e a seguran\xE7a do site. Sem eles a loja n\xE3o funciona.",
    obrigatoria: true
  },
  {
    key: "analytics",
    label: "Medi\xE7\xE3o de uso",
    descricao: "Ajudam a loja a entender quais p\xE1ginas s\xE3o usadas. Nunca identificam voc\xEA.",
    obrigatoria: false
  },
  {
    key: "marketing",
    label: "An\xFAncios",
    descricao: "Permitem medir an\xFAncios e mostrar ofertas fora do site.",
    obrigatoria: false
  }
];
var CONSENT_STORAGE_KEY = "centerpanos.consentimento";
function consentimentoRecusado(agora = /* @__PURE__ */ new Date()) {
  return {
    versao: CONSENT_VERSION,
    decididoEm: agora.toISOString(),
    analytics: false,
    marketing: false
  };
}
function consentimentoAceito(agora = /* @__PURE__ */ new Date()) {
  return {
    versao: CONSENT_VERSION,
    decididoEm: agora.toISOString(),
    analytics: true,
    marketing: true
  };
}
function lerConsentimento(bruto) {
  if (!bruto) return null;
  try {
    const dados = JSON.parse(bruto);
    if (dados.versao !== CONSENT_VERSION) return null;
    if (typeof dados.analytics !== "boolean" || typeof dados.marketing !== "boolean") return null;
    if (typeof dados.decididoEm !== "string") return null;
    return {
      versao: dados.versao,
      decididoEm: dados.decididoEm,
      analytics: dados.analytics,
      marketing: dados.marketing
    };
  } catch {
    return null;
  }
}
function permitido(consentimento, categoria) {
  if (categoria === "essencial") return true;
  if (!consentimento) return false;
  return categoria === "analytics" ? consentimento.analytics : consentimento.marketing;
}
function isSafeHref(href) {
  const limpo = href.trim();
  if (limpo.startsWith("/") && !limpo.startsWith("//")) return true;
  if (limpo.startsWith("#")) return true;
  return /^(https?:\/\/|mailto:|tel:)/i.test(limpo);
}
var INLINE = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|(?<![\w*])[*_](?![\s*_])(.+?)(?<!\s)[*_](?![\w*])/g;
function parseInline(texto) {
  const trechos = [];
  let ultimo = 0;
  for (const encontrado of texto.matchAll(INLINE)) {
    const inicio = encontrado.index ?? 0;
    if (inicio > ultimo) trechos.push({ tipo: "texto", texto: texto.slice(ultimo, inicio) });
    const [inteiro, negrito, rotulo, href, italico] = encontrado;
    if (negrito !== void 0) {
      trechos.push({ tipo: "negrito", texto: negrito });
    } else if (rotulo !== void 0 && href !== void 0) {
      trechos.push(
        isSafeHref(href) ? { tipo: "link", texto: rotulo, href: href.trim() } : { tipo: "texto", texto: rotulo }
      );
    } else if (italico !== void 0) {
      trechos.push({ tipo: "italico", texto: italico });
    } else {
      trechos.push({ tipo: "texto", texto: inteiro });
    }
    ultimo = inicio + inteiro.length;
  }
  if (ultimo < texto.length) trechos.push({ tipo: "texto", texto: texto.slice(ultimo) });
  return juntarTextos(trechos);
}
function parseMarkdown(texto) {
  const blocos = [];
  let paragrafo = [];
  let lista = null;
  const fecharParagrafo = () => {
    if (paragrafo.length > 0) {
      blocos.push({ tipo: "paragrafo", trechos: parseInline(paragrafo.join(" ")) });
      paragrafo = [];
    }
  };
  const fecharLista = () => {
    if (lista) {
      blocos.push({ tipo: "lista", ordenada: lista.ordenada, itens: lista.itens.map(parseInline) });
      lista = null;
    }
  };
  for (const bruta of texto.replace(/\r\n?/g, "\n").split("\n")) {
    const linha = bruta.trim();
    if (!linha) {
      fecharParagrafo();
      fecharLista();
      continue;
    }
    const titulo = /^(#{1,3})\s+(.+)$/.exec(linha);
    if (titulo) {
      fecharParagrafo();
      fecharLista();
      blocos.push({
        tipo: "titulo",
        nivel: titulo[1].length === 3 ? 3 : 2,
        trechos: parseInline(titulo[2])
      });
      continue;
    }
    const item = /^(?:([-*])|(\d+)[.)])\s+(.+)$/.exec(linha);
    if (item) {
      fecharParagrafo();
      const ordenada = item[2] !== void 0;
      if (lista && lista.ordenada !== ordenada) fecharLista();
      lista ??= { ordenada, itens: [] };
      lista.itens.push(item[3]);
      continue;
    }
    fecharLista();
    paragrafo.push(linha);
  }
  fecharParagrafo();
  fecharLista();
  return blocos;
}
function markdownToPlainText(texto, maximo = 160) {
  const corrido = parseMarkdown(texto).flatMap((bloco) => bloco.tipo === "lista" ? bloco.itens.flat() : bloco.trechos).map((trecho) => trecho.texto).join(" ").replace(/\s+/g, " ").trim();
  if (corrido.length <= maximo) return corrido;
  const cortado = corrido.slice(0, maximo + 1);
  const espaco = cortado.lastIndexOf(" ");
  return `${cortado.slice(0, espaco > maximo * 0.6 ? espaco : maximo).trimEnd()}\u2026`;
}
function juntarTextos(trechos) {
  const juntos = [];
  for (const trecho of trechos) {
    const anterior = juntos[juntos.length - 1];
    if (trecho.tipo === "texto" && anterior?.tipo === "texto") anterior.texto += trecho.texto;
    else if (trecho.tipo !== "texto" || trecho.texto) juntos.push({ ...trecho });
  }
  return juntos;
}
var PAGE_PATH_PREFIX = "/pagina/";
var SLUG_DE_PAGINA = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
var linkSchema = z.object({
  label: z.string().trim().min(1, "Informe o texto do link.").max(40),
  href: z.string().trim().refine(isSafeHref, "Use um caminho da loja (/ofertas) ou um endere\xE7o https.")
});
var navigationSchema = z.object({
  menu: z.array(linkSchema).max(10),
  rodape: z.array(
    z.object({ titulo: z.string().trim().min(1).max(30), links: z.array(linkSchema).max(8) })
  ).max(4)
});
var DEFAULT_NAVIGATION = {
  menu: [
    { label: "Categorias", href: "/categorias" },
    { label: "Ofertas", href: "/ofertas" },
    { label: "Cursos e workshops", href: "/cursos" },
    { label: "Minha conta", href: "/conta" }
  ],
  rodape: [
    {
      titulo: "A loja",
      links: [
        { label: "Sobre a Center Panos", href: "/pagina/sobre" },
        { label: "Cursos e workshops", href: "/cursos" },
        { label: "Ofertas", href: "/ofertas" }
      ]
    },
    {
      titulo: "Ajuda",
      links: [
        { label: "Ajuda e suporte", href: "/conta/ajuda" },
        { label: "Trocas e devolu\xE7\xF5es", href: "/pagina/trocas" },
        { label: "Privacidade", href: "/pagina/privacidade" },
        { label: "Termos de uso", href: "/pagina/termos" }
      ]
    }
  ]
};
function withPublishedPagesOnly(navegacao, publicadas) {
  const vale = (link) => {
    if (!link.href.startsWith(PAGE_PATH_PREFIX)) return true;
    const slug = link.href.slice(PAGE_PATH_PREFIX.length).split(/[?#/]/)[0] ?? "";
    return publicadas.has(slug);
  };
  return {
    menu: navegacao.menu.filter(vale),
    rodape: navegacao.rodape.map((grupo) => ({ ...grupo, links: grupo.links.filter(vale) })).filter((grupo) => grupo.links.length > 0)
  };
}
var TONS = ["marca", "destaque", "alerta"];
var janela = {
  ativo: z.boolean(),
  inicio: z.coerce.date().nullable().optional(),
  fim: z.coerce.date().nullable().optional()
};
var barraSchema = z.object({
  ...janela,
  texto: z.string().trim().min(1).max(140),
  link: z.string().trim().refine(isSafeHref, "Link inv\xE1lido.").nullable().optional(),
  tom: z.enum(TONS).default("destaque")
});
var popupSchema = z.object({
  ...janela,
  titulo: z.string().trim().min(1).max(80),
  texto: z.string().trim().max(300).default(""),
  imagemUrl: z.string().trim().url().nullable().optional(),
  ctaLabel: z.string().trim().max(30).nullable().optional(),
  ctaUrl: z.string().trim().refine(isSafeHref, "Link inv\xE1lido.").nullable().optional(),
  /** Segundos até aparecer. Pop-up que abre junto com a página é pop-up que se fecha sem ler. */
  atrasoSegundos: z.coerce.number().int().min(0).max(60).default(5)
});
var avisosSchema = z.object({
  barra: barraSchema.nullable(),
  popup: popupSchema.nullable()
});
function isLive(item, agora = /* @__PURE__ */ new Date()) {
  if (!item.ativo) return false;
  const inicio = item.inicio ? new Date(item.inicio) : null;
  const fim = item.fim ? new Date(item.fim) : null;
  if (inicio && agora < inicio) return false;
  if (fim && agora > fim) return false;
  return true;
}
var ROTAS_SEM_POPUP = ["/carrinho", "/conta", "/p/"];
function popupAllowedOn(pathname) {
  return !ROTAS_SEM_POPUP.some(
    (rota) => pathname === rota || pathname.startsWith(rota.endsWith("/") ? rota : `${rota}/`)
  );
}
function popupVersion(popup) {
  const base = [popup.titulo, popup.texto, popup.ctaUrl ?? "", popup.imagemUrl ?? ""].join("|");
  let hash = 5381;
  for (let i = 0; i < base.length; i += 1) hash = (hash << 5) + hash + base.charCodeAt(i) >>> 0;
  return hash.toString(36);
}
var HOME_SECTION_TYPES = [
  "banners",
  "categorias",
  "ofertas",
  "colecoes",
  "cursos",
  "novidades",
  "texto"
];
var HOME_SECTION_LABELS = {
  banners: "Banners",
  categorias: "Categorias",
  ofertas: "Ofertas da semana",
  colecoes: "Cole\xE7\xF5es",
  cursos: "Pr\xF3ximos cursos",
  novidades: "Novidades",
  texto: "Bloco de texto"
};
var DEFAULT_HOME_SECTIONS = [
  "banners",
  "categorias",
  "ofertas",
  "colecoes",
  "cursos",
  "novidades"
];
var BANNER_TONES = ["marca", "destaque", "escuro"];
var SUGGESTED_PAGES = [
  { slug: "sobre", title: "Sobre a Center Panos Guarulhos" },
  { slug: "trocas", title: "Trocas e devolu\xE7\xF5es" },
  { slug: "privacidade", title: "Pol\xEDtica de privacidade" },
  { slug: "termos", title: "Termos de uso" }
];

// src/courses.ts
var ENROLLMENT_STATUSES = [
  "pendente",
  "confirmada",
  "lista_de_espera",
  "cancelada"
];
var ENROLLMENT_STATUS_LABELS = {
  pendente: "Aguardando confirma\xE7\xE3o",
  confirmada: "Confirmada",
  lista_de_espera: "Lista de espera",
  cancelada: "Reembolsada"
};
var PAYMENT_METHODS = ["pix", "dinheiro", "cartao", "cortesia"];
var PAYMENT_METHOD_LABELS = {
  pix: "Pix",
  dinheiro: "Dinheiro",
  cartao: "Cart\xE3o",
  cortesia: "Cortesia"
};
var COURSE_LEVELS = ["todos", "iniciante", "intermediario", "avancado"];
var COURSE_LEVEL_LABELS = {
  todos: "Todos os n\xEDveis",
  iniciante: "Iniciante",
  intermediario: "Intermedi\xE1rio",
  avancado: "Avan\xE7ado"
};
var SEAT_STATUS_LABELS = {
  disponivel: "Vagas abertas",
  ultimas_vagas: "\xDAltimas vagas",
  lotada: "Turma lotada",
  encerrada: "Inscri\xE7\xF5es encerradas"
};
function remainingSeats(seatsTotal, confirmadas) {
  return Math.max(0, seatsTotal - confirmadas);
}
function lastSeatsThreshold(seatsTotal) {
  return Math.max(2, Math.ceil(seatsTotal * 0.2));
}
function seatStatus(turma, agora = /* @__PURE__ */ new Date()) {
  if (turma.cancelada || new Date(turma.startsAt) <= agora) return "encerrada";
  const restantes = remainingSeats(turma.seatsTotal, turma.confirmadas);
  if (restantes <= 0) return "lotada";
  if (restantes <= lastSeatsThreshold(turma.seatsTotal)) return "ultimas_vagas";
  return "disponivel";
}
function canConfirm(seatsTotal, confirmadas) {
  return confirmadas < seatsTotal;
}
function formatSessionDate(startsAt, endsAt) {
  const inicio = partes(new Date(startsAt));
  const fim = partes(new Date(endsAt));
  const mesmoDia = inicio.dia === fim.dia && inicio.mes === fim.mes;
  const horaFim = mesmoDia ? fim.hora : `${fim.dia}/${fim.mes} ${fim.hora}`;
  return `${inicio.semana}, ${inicio.dia}/${inicio.mes} \xB7 ${inicio.hora} \xE0s ${horaFim}`;
}
function formatDuration(minutos) {
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto ? `${horas}h${String(resto).padStart(2, "0")}` : `${horas}h`;
}
function buildEnrollmentMessage(dados) {
  const linhas = dados.listaDeEspera ? [`Ol\xE1! A turma de *${dados.curso}* est\xE1 lotada \u2014 quero entrar na lista de espera.`] : [`Ol\xE1! Quero me inscrever no curso *${dados.curso}*.`];
  linhas.push(`Turma: ${dados.turma}`, `Nome: ${dados.nome}`);
  if (dados.preco) linhas.push(`Valor: ${formatBRL(dados.preco)}`);
  return linhas.join("\n");
}
function partes(data) {
  const formato = new Intl.DateTimeFormat("pt-BR", {
    // A turma das 14h é às 14h em Guarulhos, esteja o servidor onde estiver.
    timeZone: STORE_TIME_ZONE,
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
  const valor = (tipo) => formato.formatToParts(data).find((parte) => parte.type === tipo)?.value ?? "";
  const minuto = valor("minute");
  const hora = String(Number(valor("hour")) % 24);
  return {
    semana: valor("weekday").replace(".", ""),
    dia: valor("day"),
    mes: valor("month"),
    hora: minuto === "00" ? `${hora}h` : `${hora}h${minuto}`
  };
}

// src/customers.ts
var PASSWORD_MIN_LENGTH = 8;
var PASSWORD_MAX_LENGTH = 128;
var COMMON_PASSWORDS = /* @__PURE__ */ new Set([
  "12345678",
  "123456789",
  "1234567890",
  "87654321",
  "11111111",
  "00000000",
  "12341234",
  "senha123",
  "senha1234",
  "senhasenha",
  "password",
  "password1",
  "qwerty123",
  "abcd1234",
  "abc12345",
  "brasil123",
  "mudar123",
  "teste123",
  "centerpanos",
  "centerpanos123",
  "iloveyou"
]);
var PASSWORD_PROBLEM_MESSAGES = {
  curta: `Use pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`,
  longa: `Use no m\xE1ximo ${PASSWORD_MAX_LENGTH} caracteres.`,
  comum: "Essa senha \xE9 uma das mais usadas \u2014 escolha outra."
};
function checkPassword(password) {
  if (password.length < PASSWORD_MIN_LENGTH) return "curta";
  if (password.length > PASSWORD_MAX_LENGTH) return "longa";
  if (COMMON_PASSWORDS.has(stripAccents(password).toLowerCase())) return "comum";
  return null;
}
function normalizeEmail(email) {
  return email.trim().toLowerCase();
}
function normalizeCep(cep) {
  return cep.replace(/\D/g, "");
}
function isValidCep(cep) {
  const digitos = normalizeCep(cep);
  return digitos.length === 8 && digitos !== "00000000";
}
function formatCep(cep) {
  const digitos = normalizeCep(cep);
  return digitos.length === 8 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : cep;
}
var UFS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO"
];
function isUf(valor) {
  return UFS.includes(valor.toUpperCase());
}
function formatAddressLine(endereco) {
  const complemento = endereco.complement ? ` (${endereco.complement})` : "";
  return `${endereco.street}, ${endereco.number}${complemento} \u2014 ${endereco.district}, ${endereco.city}/${endereco.uf}`;
}
function formatPhoneBR(telefone) {
  const digitos = telefone.replace(/\D/g, "");
  const local = digitos.length >= 12 && digitos.startsWith("55") ? digitos.slice(2) : digitos;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return telefone;
}
var NOTIFICATION_TYPES = [
  "pedido_recebido",
  "status_alterado",
  "cupom_novo",
  "conta"
];
var NOTIFICATION_TYPE_LABELS = {
  pedido_recebido: "Pedido recebido",
  status_alterado: "Pedido atualizado",
  cupom_novo: "Cupom novo",
  conta: "Sua conta"
};
var ANONYMIZED_NAME = "Cliente anonimizado";
var TERMS_VERSION = "2026-09";

// src/tasks.ts
var TASK_STATUSES = ["a_fazer", "em_andamento", "aguardando", "concluido"];
var TASK_STATUS_LABELS = {
  a_fazer: "A fazer",
  em_andamento: "Em andamento",
  aguardando: "Aguardando",
  concluido: "Conclu\xEDdo"
};
var TASK_PRIORITIES = ["alta", "normal", "baixa"];
var TASK_PRIORITY_LABELS = {
  alta: "Alta",
  normal: "Normal",
  baixa: "Baixa"
};
var TASK_KANBAN_COLUMNS = [
  { key: "a_fazer", label: "A fazer" },
  { key: "em_andamento", label: "Em andamento" },
  { key: "aguardando", label: "Aguardando" },
  { key: "concluido", label: "Conclu\xEDdo" }
];

// src/favorites.ts
var FAVORITES_STORAGE_KEY = "centerpanos:favoritos:v1";
var MAX_FAVORITES = 200;
function parseFavorites(raw) {
  if (!raw) return [];
  try {
    const dados = JSON.parse(raw);
    if (!Array.isArray(dados)) return [];
    const ids = dados.filter((item) => typeof item === "string" && item.length > 0);
    return dedupe(ids).slice(0, MAX_FAVORITES);
  } catch {
    return [];
  }
}
function addFavorite(favoritos, id) {
  if (!id) return [...favoritos];
  return dedupe([id, ...favoritos]).slice(0, MAX_FAVORITES);
}
function removeFavorite(favoritos, id) {
  return favoritos.filter((item) => item !== id);
}
function toggleFavorite(favoritos, id) {
  return favoritos.includes(id) ? removeFavorite(favoritos, id) : addFavorite(favoritos, id);
}
function isFavorite(favoritos, id) {
  return favoritos.includes(id);
}
function mergeFavorites(local, daConta) {
  return dedupe([...local, ...daConta]).slice(0, MAX_FAVORITES);
}
function dedupe(ids) {
  return [...new Set(ids)];
}

// src/reports.ts
var REPORT_RANGES = [
  { key: "hoje", label: "Hoje", dias: 0 },
  { key: "7d", label: "\xDAltimos 7 dias", dias: 6 },
  { key: "30d", label: "\xDAltimos 30 dias", dias: 29 },
  { key: "90d", label: "\xDAltimos 90 dias", dias: 89 }
];
var REPORT_RANGE_KEYS = REPORT_RANGES.map((faixa) => faixa.key);
var OFFSET_DA_LOJA = "-03:00";
function diaDaLoja(momento) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: STORE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(momento);
}
function resolveRange(chave, agora = /* @__PURE__ */ new Date()) {
  const faixa = REPORT_RANGES.find((item) => item.key === chave) ?? REPORT_RANGES[1];
  const hoje = diaDaLoja(agora);
  const fim = /* @__PURE__ */ new Date(`${hoje}T23:59:59.999${OFFSET_DA_LOJA}`);
  const primeiroDia = new Date(fim);
  primeiroDia.setUTCDate(primeiroDia.getUTCDate() - faixa.dias);
  return { inicio: /* @__PURE__ */ new Date(`${diaDaLoja(primeiroDia)}T00:00:00.000${OFFSET_DA_LOJA}`), fim };
}
function diasDoPeriodo(inicio, fim) {
  const dias = [];
  const cursor = /* @__PURE__ */ new Date(`${diaDaLoja(inicio)}T12:00:00.000${OFFSET_DA_LOJA}`);
  const ultimo = diaDaLoja(fim);
  for (let guarda = 0; guarda < 400; guarda += 1) {
    const dia = diaDaLoja(cursor);
    dias.push(dia);
    if (dia >= ultimo) break;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dias;
}
function rotuloCurtoDoDia(dia) {
  const [, mes, diaDoMes] = dia.split("-");
  return `${diaDoMes}/${mes}`;
}
function calcularFunil(bruto) {
  const divisao = (parte, todo) => todo > 0 ? parte / todo : 0;
  return {
    ...bruto,
    taxaDeAbertura: divisao(bruto.whatsapp, bruto.criados),
    taxaDeFechamento: divisao(bruto.fechados, bruto.whatsapp),
    taxaGeral: divisao(bruto.fechados, bruto.criados),
    emAberto: Math.max(0, bruto.criados - bruto.fechados - bruto.perdidos)
  };
}
function formatarTaxa(taxa) {
  return `${(taxa * 100).toFixed(1).replace(".", ",")}%`;
}
var ABC_LABELS = {
  A: "A \u2014 os que pagam a conta",
  B: "B \u2014 o meio",
  C: "C \u2014 cauda longa"
};
var CORTE_A = 0.8;
var CORTE_B = 0.95;
function classificarABC(linhas) {
  const total = linhas.reduce((soma, linha) => soma + linha.receita, 0);
  if (total <= 0) {
    return linhas.map((linha) => ({
      ...linha,
      classe: "C",
      participacao: 0,
      acumulado: 0
    }));
  }
  const ordenadas = [...linhas].sort((a, b) => b.receita - a.receita);
  let acumulado = 0;
  return ordenadas.map((linha) => {
    const anterior = acumulado;
    const participacao = linha.receita / total;
    acumulado += participacao;
    const classe = anterior < CORTE_A ? "A" : anterior < CORTE_B ? "B" : "C";
    return { ...linha, classe, participacao, acumulado };
  });
}
var BOM = String.fromCharCode(65279);
function montarCsv(colunas, linhas) {
  const cabecalho = colunas.map((coluna) => toCsvCell(coluna.label)).join(";");
  const corpo = linhas.map(
    (linha) => colunas.map((coluna) => toCsvCell(coluna.valor(linha))).join(";")
  );
  return `${BOM}${[cabecalho, ...corpo].join("\r\n")}\r
`;
}
function nomeDoArquivo(relatorio, inicio, fim) {
  return `${relatorio}-${diaDaLoja(inicio)}-a-${diaDaLoja(fim)}.csv`;
}
var REPORTS = [
  {
    key: "vendas",
    label: "Vendas por per\xEDodo",
    descricao: "Pedidos, valor fechado e ticket m\xE9dio, dia a dia."
  },
  {
    key: "funil",
    label: "Funil do WhatsApp",
    descricao: "De pedido criado a venda fechada, com as taxas de cada etapa."
  },
  {
    key: "abc",
    label: "Curva ABC",
    descricao: "Quais produtos pagam a conta e quais s\xF3 ocupam prateleira."
  },
  {
    key: "ruptura",
    label: "Ruptura de estoque",
    descricao: "Sem estoque ou abaixo do limiar, com o quanto j\xE1 foi procurado."
  },
  {
    key: "buscas",
    label: "Buscas sem resultado",
    descricao: "O que as clientes procuraram e a loja n\xE3o tinha."
  },
  {
    key: "sem-foto",
    label: "Produtos sem foto",
    descricao: "Ativos na vitrine sem imagem nenhuma."
  },
  {
    key: "sem-venda",
    label: "Produtos sem venda",
    descricao: "Ativos que n\xE3o venderam no per\xEDodo."
  }
];

// src/search.ts
var MIN_SEARCH_LENGTH = 2;
var MAX_SEARCH_LENGTH = 120;
var MAX_SUGGESTIONS = 8;
function sanitizeSearchTerm(input) {
  return input.replace(/\s+/g, " ").trim().slice(0, MAX_SEARCH_LENGTH);
}
function isSearchable(input) {
  return sanitizeSearchTerm(input).length >= MIN_SEARCH_LENGTH;
}
function searchWords(input) {
  return normalizeSearchTerm(sanitizeSearchTerm(input)).split(" ").filter((palavra) => palavra.length >= MIN_SEARCH_LENGTH);
}
function highlightMatches(text, term) {
  const palavras = searchWords(term);
  if (palavras.length === 0 || !text) return [{ text, match: false }];
  const alvo = normalizeSearchTerm(text);
  const marcado = new Array(text.length).fill(false);
  for (const palavra of palavras) {
    let de = alvo.indexOf(palavra);
    while (de !== -1) {
      for (let i = de; i < de + palavra.length && i < marcado.length; i += 1) marcado[i] = true;
      de = alvo.indexOf(palavra, de + palavra.length);
    }
  }
  const segmentos = [];
  for (let i = 0; i < text.length; i += 1) {
    const ultimo = segmentos[segmentos.length - 1];
    if (ultimo && ultimo.match === marcado[i]) ultimo.text += text[i];
    else segmentos.push({ text: text[i], match: marcado[i] });
  }
  return segmentos;
}

// src/whatsapp.ts
var MAX_ITEMS_IN_FULL_MESSAGE = 8;
var SAFE_ENCODED_LENGTH = 1600;
function buildFullOrderMessage(payload) {
  const lines = [];
  lines.push(`Ol\xE1! Quero finalizar meu pedido *${payload.code}* \u{1F9F5}`);
  lines.push("");
  payload.items.forEach((item, index) => {
    const pack = formatPackSize(item.packSize);
    const name = [item.name, item.variantName, pack ? `(${pack})` : null].filter(Boolean).join(" ");
    lines.push(`${index + 1}. ${name}`);
    lines.push(
      `   ${formatQuantity(item.quantity, item.unit)} \xD7 ${formatBRL(item.unitPrice)} = ${formatBRL(item.lineTotal)}`
    );
  });
  lines.push("");
  lines.push(
    `Subtotal (${countItems(payload)} ${itemWord(payload)}): ${formatBRL(payload.subtotal)}`
  );
  if (payload.discount && payload.discount > 0) {
    lines.push(`Desconto: -${formatBRL(payload.discount)}`);
  }
  lines.push(`Entrega: ${deliveryLine(payload)}`);
  lines.push(`*Total: ${formatBRL(payload.total)}*`);
  lines.push("");
  lines.push(`Detalhes: ${payload.orderUrl}`);
  return finish(lines, payload);
}
function buildCompactOrderMessage(payload) {
  const lines = [];
  lines.push(`Ol\xE1! Quero finalizar meu pedido *${payload.code}* \u{1F9F5}`);
  lines.push("");
  lines.push(`${countItems(payload)} ${itemWord(payload)} \xB7 *${formatBRL(payload.total)}*`);
  lines.push(`Entrega: ${deliveryLine(payload)}`);
  lines.push("");
  lines.push(`Lista completa do pedido: ${payload.orderUrl}`);
  return finish(lines, payload);
}
function buildOrderWhatsAppLink(phone, payload) {
  if (payload.items.length <= MAX_ITEMS_IN_FULL_MESSAGE) {
    const message2 = buildFullOrderMessage(payload);
    if (encodeURIComponent(message2).length <= SAFE_ENCODED_LENGTH) {
      return { url: buildWhatsAppUrl(phone, message2), message: message2, compact: false };
    }
  }
  const message = buildCompactOrderMessage(payload);
  return { url: buildWhatsAppUrl(phone, message), message, compact: true };
}
function buildWhatsAppUrl(phone, message) {
  return `https://wa.me/${normalizePhone(phone)}?text=${encodeURIComponent(message)}`;
}
function buildContactUrl(phone, message) {
  return buildWhatsAppUrl(
    phone,
    message ?? "Ol\xE1! Vim pelo site da Center Panos Guarulhos e gostaria de uma informa\xE7\xE3o."
  );
}
function buildProductInquiryUrl(phone, product) {
  const price = product.price !== void 0 ? ` (${formatBRL(product.price)})` : "";
  const lines = [`Ol\xE1! Tenho interesse neste produto: *${product.name}*${price}`];
  if (product.variantName) lines.push(`Varia\xE7\xE3o: ${product.variantName}`);
  if (product.quantity !== void 0 && product.unit) {
    const total = product.lineTotal !== void 0 ? ` \u2014 total ${formatBRL(product.lineTotal)}` : "";
    lines.push(`Quantidade: ${formatQuantity(product.quantity, product.unit)}${total}`);
  }
  lines.push(product.url);
  return buildWhatsAppUrl(phone, lines.join("\n"));
}
function normalizePhone(phone) {
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("55")) return digits;
  return `55${digits}`;
}
function deliveryLine(payload) {
  const note = payload.deliveryNote?.trim();
  return note ? `${payload.deliveryLabel} (${note})` : payload.deliveryLabel;
}
function countItems(payload) {
  return payload.items.length;
}
function itemWord(payload) {
  return countItems(payload) === 1 ? "item" : "itens";
}
function finish(lines, payload) {
  if (payload.customerName) {
    lines.push("");
    lines.push(`Meu nome: ${payload.customerName}`);
  }
  if (payload.closedNotice) {
    lines.push("");
    lines.push(payload.closedNotice);
  }
  return lines.join("\n");
}
var phoneSchema = z.string().trim().transform((value) => value.replace(/\D/g, "")).refine((digits) => digits.length === 10 || digits.length === 11, {
  message: "Informe um telefone com DDD, ex.: (11) 91132-8999"
});
var cepSchema = z.string().trim().transform((value) => value.replace(/\D/g, "")).refine((digits) => digits.length === 8, { message: "CEP deve ter 8 d\xEDgitos" });
var brandingSchema = z.object({
  logoPrincipal: z.string().url().nullable().default(null),
  logoRodape: z.string().url().nullable().default(null),
  logoMono: z.string().url().nullable().default(null),
  favicon: z.string().url().nullable().default(null),
  ogImage: z.string().url().nullable().default(null),
  iconesPwa: z.array(z.object({ size: z.number().int(), url: z.string().url() })).default([]),
  corPrimaria: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#1B3A8C"),
  corSecundaria: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#FDC300"),
  corDestaque: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#E30613")
});
var businessHourSchema = z.object({
  weekday: z.number().int().min(0).max(6),
  opensAt: z.string().regex(/^\d{2}:\d{2}$/),
  closesAt: z.string().regex(/^\d{2}:\d{2}$/),
  closed: z.boolean()
});
var storeInfoSchema = z.object({
  name: z.string(),
  legalName: z.string(),
  cnpj: z.string(),
  whatsapp: z.string(),
  phone: z.string().nullable(),
  email: z.string().email().nullable(),
  address: z.object({
    street: z.string(),
    number: z.string(),
    district: z.string(),
    city: z.string(),
    uf: z.string().length(2),
    cep: z.string()
  }),
  instagram: z.string().nullable(),
  facebook: z.string().nullable().default(null),
  whatsappBotao: z.object({
    ativo: z.boolean().default(true),
    mensagem: z.string().max(300).default("")
  }).default({ ativo: true, mensagem: "" }),
  msgFeriado: z.string().max(200).nullable().default(null),
  feriadoAtivo: z.boolean().default(false),
  // Texto por status de pedido (chave = status), enviado ao cliente no WhatsApp.
  mensagensStatus: z.record(z.string(), z.string().max(1e3)).default({})
});
var deliveryMethodSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  type: z.enum(DELIVERY_TYPES),
  /** "Grátis" | "Consultar valor" — texto, nunca um cálculo. */
  priceLabel: z.string(),
  asksAddress: z.boolean(),
  position: z.number().int()
});
var createOrderItemSchema = z.object({
  productId: z.string().uuid(),
  variantId: z.string().uuid().nullable().optional(),
  quantity: z.number().positive()
});
var createOrderSchema = z.object({
  name: z.string().trim().min(2, "Informe seu nome"),
  phone: phoneSchema,
  email: z.string().email().optional().nullable(),
  deliveryMethodId: z.string(),
  deliveryNote: z.string().trim().max(140).optional().nullable(),
  couponCode: z.string().trim().max(40).optional().nullable(),
  notes: z.string().trim().max(500).optional().nullable(),
  items: z.array(createOrderItemSchema).min(1, "O carrinho est\xE1 vazio")
});
var saleUnitSchema = z.enum(SALE_UNITS);
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(24)
});

export { ABC_LABELS, ACTION_LABELS, ANONYMIZED_NAME, AVAILABILITIES, AVAILABILITY_LABELS, BANNER_TONES, CLOSED_ORDER_STATUSES, CONSENT_CATEGORIES, CONSENT_STORAGE_KEY, CONSENT_VERSION, COUPON_REJECTION_MESSAGES, COURSE_LEVELS, COURSE_LEVEL_LABELS, DEFAULT_HOME_SECTIONS, DEFAULT_NAVIGATION, DEFAULT_ROLES, DEFAULT_SCHEDULE, DEFAULT_STATUS_MESSAGES, DELIVERY_TYPES, ENROLLMENT_STATUSES, ENROLLMENT_STATUS_LABELS, EXPORT_FORMATS, FAVORITES_STORAGE_KEY, FIELDS_BY_KIND, HOME_SECTION_LABELS, HOME_SECTION_TYPES, IMPORT_FIELDS, IMPORT_FIELD_LABELS, IMPORT_KINDS, KANBAN_COLUMNS, MAX_FAVORITES, MAX_ITEMS_IN_FULL_MESSAGE, MAX_SEARCH_LENGTH, MAX_SUGGESTIONS, MIN_SEARCH_LENGTH, MODULE_LABELS, NOTIFICATION_TYPES, NOTIFICATION_TYPE_LABELS, ORDER_STATUSES, ORDER_STATUS_LABELS, PAGE_PATH_PREFIX, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PASSWORD_PROBLEM_MESSAGES, PAYMENT_METHODS, PAYMENT_METHOD_LABELS, PERMISSION_ACTIONS, PERMISSION_MODULES, PRODUCT_STATUSES, RECOVERY_AFTER_HOURS, REPORTS, REPORT_RANGES, REPORT_RANGE_KEYS, SAFE_ENCODED_LENGTH, SALE_UNITS, SALE_UNIT_LABELS, SEAT_STATUS_LABELS, SLUG_DE_PAGINA, STORE_TIME_ZONE, SUGGESTED_PAGES, TASK_KANBAN_COLUMNS, TASK_PRIORITIES, TASK_PRIORITY_LABELS, TASK_STATUSES, TASK_STATUS_LABELS, TERMS_VERSION, TONS, UFS, acceptsFinalValue, addFavorite, allPermissions, applyCoupon, applyMarkup, avisosSchema, barraSchema, brandingSchema, buildCompactOrderMessage, buildContactUrl, buildEnrollmentMessage, buildFullOrderMessage, buildOrderWhatsAppLink, buildProductInquiryUrl, buildWhatsAppUrl, businessHourSchema, calcularFunil, calculateTotals, canConfirm, canTransition, cepSchema, checkPassword, classificarABC, consentimentoAceito, consentimentoRecusado, createOrderItemSchema, createOrderSchema, decrementQuantity, defaultRulesFor, deliveryMethodSchema, diaDaLoja, diasDoPeriodo, diffRow, discountPercent, escapeCsvInjection, expandPermissions, formatAddressLine, formatAmount, formatBRL, formatCep, formatDuration, formatOrderCode, formatPackSize, formatPhoneBR, formatQuantity, formatScheduleLines, formatSessionDate, formatTime, formatUnitPriceSuffix, formatarTaxa, getStoreStatus, guessColumnMap, hasAllPermissions, hasAnyPermission, hasPermission, highlightMatches, incrementQuantity, isClosedStatus, isFavorite, isLive, isPromoActive, isSafeHref, isSearchable, isUf, isValidCep, lastSeatsThreshold, lerConsentimento, lineTotal, markdownToPlainText, mergeFavorites, montarCsv, multiplyQuantity, navigationSchema, needsRecovery, nomeDoArquivo, normalizeAvailability, normalizeCep, normalizeEmail, normalizePhone, normalizeQuantity, normalizeQuantityValue, normalizeSearchTerm, normalizeStatus, normalizeUnit, paginationSchema, parseBRL, parseFavorites, parseInline, parseMarkdown, parseOrderCode, parseRow, permitido, phoneSchema, popupAllowedOn, popupSchema, popupVersion, preencherMensagem, remainingSeats, removeFavorite, requiresLostReason, resolvePrice, resolveRange, rotuloCurtoDoDia, saleUnitSchema, sanitizeSearchTerm, searchWords, seatStatus, slugify, storeInfoSchema, stripAccents, sumCents, summarize, titleCaseFromErp, toCsvCell, toggleFavorite, withPublishedPagesOnly };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map